/**
 * @fileoverview 行情跑马灯组件
 * @description 显示实时行情价格滚动条，展示各资产最新价格和涨跌幅
 * 设计风格：深黑背景，绿涨红跌，无限循环滚动
 * @module components/layout/TickerBar
 */

import { TrendingDown, TrendingUp } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { cn } from '../../lib/utils';
import { fetchClosePriceSet, type ClosePriceItem } from '../../services/marketService';
import { API_OK } from '../../services/api';

// ============================================================
// 子组件：单个行情项
// ============================================================

interface TickerItemProps {
  symbol: string;
  price: number;
  change: number;
}

/**
 * 单个行情项组件
 * 显示资产符号、价格和涨跌幅
 */
function TickerItem({ symbol, price, change }: TickerItemProps) {
  const isUp = change >= 0;

  const formatPrice = (p: number): string => {
    if (!Number.isFinite(p) || p <= 0) return '-';
    if (p >= 100) return p.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    if (p >= 1) return p.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
    return p.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 6 });
  };

  return (
    <span className="inline-flex items-center gap-2 px-4 whitespace-nowrap">
      <span className="text-muted-foreground text-xs font-medium">{symbol}</span>
      <span className="text-foreground text-xs font-mono font-medium">
        ${formatPrice(price)}
      </span>
      <span className={cn('inline-flex items-center gap-0.5 text-xs font-mono', isUp ? 'text-up' : 'text-down')}>
        {isUp ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
        {isUp ? '+' : ''}{change.toFixed(2)}%
      </span>
    </span>
  );
}

// ============================================================
// 主组件：行情跑马灯
// ============================================================

/** 轮询间隔（毫秒），服务端缓存 5 分钟，前端每 30 秒刷新一次 */
const POLL_INTERVAL_MS = 30_000;

export default function TickerBar() {
  const [items, setItems] = useState<ClosePriceItem[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const loadData = async () => {
    try {
      const resp = await fetchClosePriceSet();
      if (resp.status === API_OK && resp.data?.items?.length) {
        setItems(resp.data.items);
      }
    } catch {
      // 静默失败，保留上次数据
    }
  };

  useEffect(() => {
    loadData();
    timerRef.current = setInterval(loadData, POLL_INTERVAL_MS);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  if (!items.length) {
    return (
      <div className="bg-surface-1 border-b border-border overflow-hidden h-8 flex items-center">
        <div className="flex-shrink-0 px-3 border-r border-border h-full flex items-center">
          <span className="text-brand text-xs font-semibold whitespace-nowrap">LIVE</span>
        </div>
        <div className="flex-1 flex items-center px-4">
          <span className="text-muted-foreground text-xs animate-pulse">Loading market data...</span>
        </div>
      </div>
    );
  }

  const doubledItems = [...items, ...items];

  return (
    <div className="bg-surface-1 border-b border-border overflow-hidden h-8 flex items-center">
      <div className="flex-shrink-0 px-3 border-r border-border h-full flex items-center">
        <span className="text-brand text-xs font-semibold whitespace-nowrap">LIVE</span>
      </div>
      <div className="flex-1 overflow-hidden relative">
        <div className="ticker-scroll flex">
          {doubledItems.map((item, index) => (
            <TickerItem
              key={`${item.symbol}-${index}`}
              symbol={item.symbol}
              price={item.latestPrice}
              change={item.changePercent}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
